import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

// Task interface matching the tasks table structure
export interface Task {
  id: string;
  name: string;
  description?: string;
  status: 'todo' | 'inProgress' | 'done' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  due_date?: string;
  assigned_to?: string;
  assignee?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  owner?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  user_id: string;
  organization_uuid: string;
  custom_fields?: Record<string, any>;
  relations?: {
    candidates?: string[];
    matches?: string[];
    jobs?: string[];
    companies?: string[];
    calendar?: string[];
  };
  linked_users?: Array<{
    user_id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  }>;
  created_at: string;
  updated_at: string;
}

// Fetch tasks
export const useTasks = (ownershipFilter: 'my-tasks' | 'assigned-to-me' | 'all' = 'all') => {
  return useQuery({
    queryKey: ['tasks', ownershipFilter],
    queryFn: async (): Promise<Task[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      let query = supabase
        .from('tasks')
        .select('*');

      // Apply ownership filter
      if (ownershipFilter === 'my-tasks') {
        // Only tasks created by me
        query = query.eq('user_id', user.id);
      }
      // For 'assigned-to-me' and 'all', we fetch all tasks and filter client-side
      // to include linked tasks from task_users table

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      // Apply client-side filtering based on ownership
      let filteredData = data || [];
      
      if (ownershipFilter === 'assigned-to-me') {
        // For assigned-to-me: only tasks where assigned_to = current user
        filteredData = filteredData.filter(task => task.assigned_to === user.id);
      } else if (ownershipFilter === 'all') {
        // For all: get tasks where user is linked via task_users table
        const { data: linkedTasks, error: linkedError } = await supabase
          .from('task_users')
          .select('task_id')
          .eq('user_id', user.id);
        
        if (linkedError) {
          console.error('Error fetching linked tasks:', linkedError);
        }
        
        const linkedTaskIds = new Set<string>((linkedTasks || []).map(t => t.task_id));
        
        // For all: include tasks where created OR assigned OR linked
        filteredData = filteredData.filter(task => {
          const isCreator = task.user_id === user.id;
          const isAssigned = task.assigned_to === user.id;
          const isLinked = linkedTaskIds.has(task.id);
          return isCreator || isAssigned || isLinked;
        });
      }

      // If we have tasks, fetch both assignee and owner information
      if (filteredData && filteredData.length > 0) {
        const assigneeIds = filteredData
          .filter(task => task.assigned_to)
          .map(task => task.assigned_to);
        
        const ownerIds = filteredData.map(task => task.user_id);
        
        // Combine and remove duplicates
        const allUserIds = [...new Set([...assigneeIds, ...ownerIds])];

        if (allUserIds.length > 0) {
          try {
            const { data: userData, error: userError } = await supabase
              .rpc('get_multiple_users_display_info', {
                p_user_ids: allUserIds
              });

            if (!userError && userData) {
              // Create a map of user_id to user info
              const userMap = new Map(
                userData.map(user => [user.user_id, user])
              );

              // Fetch linked users for each task
              const taskIds = filteredData.map(t => t.id);
              const { data: linkedUsersData } = await supabase
                .from('task_users')
                .select('task_id, user_id')
                .in('task_id', taskIds);

              // Create a map of task_id to linked user_ids
              const linkedUsersMap = new Map<string, string[]>();
              (linkedUsersData || []).forEach(link => {
                if (!linkedUsersMap.has(link.task_id))
                  linkedUsersMap.set(link.task_id, []);
                linkedUsersMap.get(link.task_id)?.push(link.user_id);
              });

              // Get unique user IDs from linked users
              const linkedUserIds = [...new Set((linkedUsersData || []).map(l => l.user_id))];
              
              // Fetch user info for linked users if needed
              let linkedUsersInfoMap = new Map();
              if (linkedUserIds.length > 0) {
                const { data: linkedUsersInfo } = await supabase
                  .rpc('get_multiple_users_display_info', {
                    p_user_ids: linkedUserIds
                  });
                if (linkedUsersInfo)
                  linkedUsersInfoMap = new Map(linkedUsersInfo.map(u => [u.user_id, u]));
              }

              // Attach assignee, owner, and linked users info to tasks
              return filteredData.map(task => ({
                ...task,
                assignee: task.assigned_to ? userMap.get(task.assigned_to) : undefined,
                owner: userMap.get(task.user_id),
                linked_users: (linkedUsersMap.get(task.id) || []).map(userId => 
                  linkedUsersInfoMap.get(userId)
                ).filter(Boolean)
              }));
            }
          } catch (userError) {
            console.warn('Failed to fetch user information:', userError);
          }
        }
      }

      return filteredData || [];
    },
  });
};

// Create task
export const useCreateTask = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (taskData: Omit<Task, 'id' | 'user_id' | 'organization_uuid' | 'created_at' | 'updated_at'> & { linkedUsers?: string[] }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get user's organization
      const { data: userOrg } = await supabase
        .from('user_organization')
        .select('organization_uuid')
        .eq('user_uuid', user.id)
        .single();

      if (!userOrg) throw new Error('User not associated with any organization');

      const { linkedUsers, ...taskDataWithoutLinkedUsers } = taskData;

      const { data, error } = await supabase
        .from('tasks')
        .insert({
          ...taskDataWithoutLinkedUsers,
          user_id: user.id,
          organization_uuid: userOrg.organization_uuid,
        })
        .select()
        .single();

      if (error) {
        throw new Error(error.message);
      }

      // Link users to the task if linkedUsers is provided
      if (linkedUsers && linkedUsers.length > 0 && data) {
        const userLinks = linkedUsers.map(userId => ({
          task_id: data.id,
          user_id: userId,
          linked_by: user.id,
        }));

        const { error: linkError } = await supabase
          .from('task_users')
          .insert(userLinks);

        if (linkError) {
          throw new Error(`Task created but failed to link users: ${linkError.message}`);
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['overdue-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['due-tasks'] });
      toast({
        title: "Success",
        description: "Task created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// Update task
export const useUpdateTask = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, linkedUsers, assignee, ...taskData }: Partial<Task> & { id: string; linkedUsers?: string[] }) => {
      // Filter out any fields that aren't actual columns in the tasks table
      const validFields = [
        'name', 'description', 'status', 'priority', 'due_date', 
        'assigned_to', 'user_id', 'custom_fields', 'relations'
      ];
      
      const cleanTaskData = Object.keys(taskData)
        .filter(key => validFields.includes(key))
        .reduce((obj, key) => {
          obj[key] = taskData[key];
          return obj;
        }, {} as Record<string, any>);

      // Update the task itself
      const { error } = await supabase
        .from('tasks')
        .update(cleanTaskData)
        .eq('id', id);

      if (error) {
        throw new Error(error.message);
      }

      // Handle linked users if provided
      if (linkedUsers !== undefined) {
        // First, delete all existing task_users links
        const { error: deleteError } = await supabase
          .from('task_users')
          .delete()
          .eq('task_id', id);

        if (deleteError) {
          throw new Error(`Task updated but failed to manage user links: ${deleteError.message}`);
        }

        // Then, insert new links
        if (linkedUsers.length > 0) {
          const { data: { user } } = await supabase.auth.getUser();
          const userLinks = linkedUsers.map(userId => ({
            task_id: id,
            user_id: userId,
            linked_by: user?.id || null
          }));

          const { error: linkError } = await supabase
            .from('task_users')
            .insert(userLinks);

          if (linkError) {
            throw new Error(`Task updated but failed to link users: ${linkError.message}`);
          }
        }
      }

      // Fetch the updated task to return
      const { data, error: fetchError } = await supabase
        .from('tasks')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      return data;
    },
    onSuccess: async (data, variables) => {
      // Wait for queries to invalidate and refetch before showing success
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['overdue-tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['due-tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['task-linked-users', variables.id] })
      ]);
      
      toast({
        title: "Success",
        description: "Task updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// Delete task
export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id);

      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['overdue-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['due-tasks'] });
      toast({
        title: "Success",
        description: "Task deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
